import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Users, 
  UserCheck, 
  UserPlus,
  Clock, 
  TrendingUp, 
  LogOut,
  ChevronRight,
  BarChart3,
  Activity,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Share2,
  BookOpen,
  Briefcase,
  RefreshCw,
  Database,
  AlertTriangle,
  CheckCircle,
  Play,
  HelpCircle,
  Info,
  Zap,
  Users2,
  FileSearch,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

// Help Modal Content for Job Matching System
const JobMatchingHelpModal = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <HelpCircle className="h-5 w-5 text-slate-400 hover:text-blue-500" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Briefcase className="h-5 w-5 text-blue-600" />
            Job Matching System Guide
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* How It Works */}
          <section>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-amber-500" />
              How Job Matching Works
            </h3>
            <div className="text-sm text-slate-600 space-y-2 pl-6">
              <p>1. <strong>User completes onboarding</strong> → Profile auto-syncs to matching database</p>
              <p>2. <strong>V-FIT Algorithm</strong> scores each job (0-100) based on user preferences</p>
              <p>3. <strong>Top 20 matches</strong> are stored per user, ranked by score</p>
              <p>4. <strong>Match Strength:</strong> Strong (80+), Clear (60-79), Emerging (40-59)</p>
            </div>
          </section>

          {/* Understanding the Stats */}
          <section>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-blue-500" />
              Understanding the Stats
            </h3>
            <div className="text-sm text-slate-600 space-y-2 pl-6">
              <p><strong>Total Jobs:</strong> All jobs loaded in Supabase (your job database)</p>
              <p><strong>Eligible Jobs:</strong> Jobs available for matching (same as total - all jobs are eligible)</p>
              <p><strong>Matchable Users:</strong> Users with completed profiles synced to matching system</p>
              <p><strong>Match Results:</strong> Total job-user match pairs stored (up to 20 per user)</p>
            </div>
          </section>

          {/* Common Issues */}
          <section>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Common Issues &amp; Solutions
            </h3>
            <div className="text-sm space-y-3 pl-6">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="font-medium text-amber-800">&quot;X users not synced to Supabase&quot;</p>
                <p className="text-amber-700 mt-1">These users completed onboarding before auto-sync was enabled. Click <strong>&quot;Sync All Users&quot;</strong> to fix.</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="font-medium text-blue-800">User reports &quot;No matches&quot;</p>
                <p className="text-blue-700 mt-1">Ask them to click <strong>&quot;Refresh&quot;</strong> on their Job Matches page. This re-syncs their profile and recalculates matches.</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="font-medium text-purple-800">User reports &quot;Poor quality matches&quot;</p>
                <p className="text-purple-700 mt-1">Review their preferences in their profile. Narrow criteria = fewer but better matches. Broad criteria = more but varied matches.</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="font-medium text-slate-800">After importing new jobs</p>
                <p className="text-slate-600 mt-1">Go to <strong>Job Import</strong> to upload a new CSV. The import clears old jobs and match results. Then click <strong>&quot;Run Matching Job&quot;</strong> here to recalculate matches for all users.</p>
              </div>
            </div>
          </section>

          {/* Admin Actions */}
          <section>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <Play className="h-4 w-4 text-lime-500" />
              Admin Actions Explained
            </h3>
            <div className="text-sm text-slate-600 space-y-2 pl-6">
              <p><strong>Sync All Users:</strong> Pushes all completed MongoDB profiles to Supabase. Use after seeing &quot;users not synced&quot; warning.</p>
              <p><strong>Run Matching Job:</strong> Recalculates V-FIT scores for ALL users against ALL jobs. Use after bulk job imports.</p>
              <p><strong>Refresh Status:</strong> Reloads the diagnostic stats on this panel.</p>
            </div>
          </section>

          {/* User Self-Service */}
          <section>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-2">
              <Users2 className="h-4 w-4 text-indigo-500" />
              User Self-Service
            </h3>
            <div className="text-sm text-slate-600 pl-6">
              <p>Users can click the <strong>&quot;Refresh&quot;</strong> button on their Job Matches page at any time to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Re-sync their profile after updating preferences</li>
                <li>Recalculate their matches against all current jobs</li>
                <li>Get fresh results if they had zero matches before</li>
              </ul>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Job Matching Controls Component
const JobMatchingControls = ({ authAxios }) => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionProgress, setActionProgress] = useState(null);

  const loadDiagnostics = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get('/admin/matching/diagnostics');
      setDiagnostics(response.data);
    } catch (error) {
      console.error('Failed to load diagnostics:', error);
      toast.error('Failed to load matching diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios]);

  const pollMatchingRun = async (jobRunId, action) => {
    const POLL_MS = 2500;
    const MAX_POLLS = 600; // 25 minutes upper bound
    let polls = 0;
    while (polls < MAX_POLLS) {
      polls += 1;
      try {
        const res = await authAxios.get(`/admin/matching/run/${jobRunId}`);
        const d = res.data || {};
        if (d.status === 'completed') {
          const successMessage = `Matching complete! ${d.matches_created} matches created for ${d.users_processed} users`;
          const details = `${d.strong_matches || 0} strong matches found`;
          setActionProgress({
            action,
            status: 'complete',
            message: successMessage,
            stats: {
              usersProcessed: d.users_processed,
              matchesCreated: d.matches_created,
              strongMatches: d.strong_matches,
            },
          });
          toast.success(successMessage, { description: details });
          setTimeout(async () => {
            await loadDiagnostics();
            setTimeout(() => setActionProgress(null), 3000);
          }, 500);
          return;
        }
        if (d.status === 'failed') {
          const msg = d.error_message ? `${action} failed: ${d.error_message}` : `${action} failed on the server`;
          setActionProgress({ action, status: 'error', message: msg });
          toast.error(`Failed to ${action}`, { description: d.error_message || undefined });
          setTimeout(() => setActionProgress(null), 8000);
          return;
        }
        // Still running — update progress banner
        setActionProgress({
          action,
          status: 'starting',
          message: `Matching in progress — ${d.users_processed || 0} users processed, ${d.matches_created || 0} matches created…`,
        });
      } catch (err) {
        // Transient polling error — try again
        console.warn('Poll error (will retry):', err);
      }
      await new Promise(r => setTimeout(r, POLL_MS));
    }
    // Timed out client-side; the job may still be running on the server
    setActionProgress({
      action,
      status: 'error',
      message: 'Matching is still running on the server. Check back in a few minutes.',
    });
    toast.message('Matching is still running', {
      description: 'It will complete in the background. Refresh the page in a few minutes.',
      duration: 10000,
    });
  };

  const handleAction = async (action, endpoint) => {
    setActionLoading(action);
    setActionProgress({ action, status: 'starting', message: `Starting ${action.toLowerCase()}...` });
    
    try {
      const response = await authAxios.post(endpoint);
      const data = response.data;
      
      // ── Background-job path: "Run Matching" returns immediately with job_run_id ──
      if (action === 'Run Matching' && data && data.job_run_id) {
        toast.message('Matching started', {
          description: 'Running in the background — this can take a few minutes.',
        });
        // Poll in the background; release the button now so admin isn't stuck on a spinner
        setActionLoading(null);
        await pollMatchingRun(data.job_run_id, action);
        return;
      }
      
      // Build detailed success message based on action type
      let successMessage = `${action} completed!`;
      let details = null;
      
      if (action === 'Sync Users') {
        const synced = data.synced || data.users_synced || 0;
        const totalFound = data.total_found || 0;
        const errors = data.errors || 0;
        const errorDetails = data.error_details || [];
        
        if (synced > 0) {
          successMessage = `Synced ${synced} user${synced !== 1 ? 's' : ''} to matching database`;
        } else if (totalFound === 0) {
          successMessage = 'No users found to sync';
        } else {
          successMessage = `Sync attempted for ${totalFound} users`;
        }
        
        if (errors > 0) {
          details = `${errors} error${errors !== 1 ? 's' : ''} occurred`;
          // Log error details to console for debugging
          console.error('Sync errors:', errorDetails);
          // Show first error in toast
          if (errorDetails.length > 0) {
            details = errorDetails[0];
          }
        }
        
        setActionProgress({ 
          action, 
          status: errors > 0 && synced === 0 ? 'error' : 'complete', 
          message: successMessage,
          stats: { synced, errors, totalFound },
          errorDetails: errorDetails
        });
      } else if (action === 'Run Matching') {
        const usersProcessed = data.users_processed || 0;
        const matchesCreated = data.matches_created || 0;
        const strongMatches = data.strong_matches || 0;
        successMessage = `Matching complete! ${matchesCreated} matches created for ${usersProcessed} users`;
        details = `${strongMatches} strong matches found`;
        setActionProgress({ 
          action, 
          status: 'complete', 
          message: successMessage,
          stats: { usersProcessed, matchesCreated, strongMatches }
        });
      }
      
      if (data.errors > 0 && data.synced === 0) {
        toast.error(successMessage, { description: details, duration: 10000 });
      } else if (data.errors > 0) {
        toast.warning(successMessage, { description: details, duration: 8000 });
      } else {
        toast.success(successMessage, { description: details });
      }
      
      // Refresh diagnostics after short delay to show progress
      setTimeout(async () => {
        await loadDiagnostics();
        // Clear progress after diagnostics refresh
        setTimeout(() => setActionProgress(null), 3000);
      }, 500);
      
    } catch (error) {
      console.error(`Failed to ${action}:`, error);
      toast.error(`Failed to ${action}`);
      setActionProgress({ action, status: 'error', message: `${action} failed. Please try again.` });
      setTimeout(() => setActionProgress(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const getHealthBadge = (health) => {
    switch (health) {
      case 'OK':
        return <Badge className="bg-lime-100 text-lime-700"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>;
      case 'WARNING':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" />Warning</Badge>;
      case 'CRITICAL':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" />Critical</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            Job Matching System
            {diagnostics && getHealthBadge(diagnostics.health)}
            <JobMatchingHelpModal />
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadDiagnostics}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Action Progress Banner */}
        {actionProgress && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${
            actionProgress.status === 'starting' ? 'bg-blue-50 border border-blue-200' :
            actionProgress.status === 'complete' ? 'bg-lime-50 border border-lime-200' :
            'bg-red-50 border border-red-200'
          }`}>
            {actionProgress.status === 'starting' && (
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            )}
            {actionProgress.status === 'complete' && (
              <CheckCircle className="h-5 w-5 text-lime-600" />
            )}
            {actionProgress.status === 'error' && (
              <AlertTriangle className="h-5 w-5 text-red-600" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                actionProgress.status === 'starting' ? 'text-blue-800' :
                actionProgress.status === 'complete' ? 'text-lime-800' :
                'text-red-800'
              }`}>
                {actionProgress.message}
              </p>
              {actionProgress.stats && actionProgress.action === 'Sync Users' && (
                <p className="text-xs text-lime-600 mt-1">
                  {actionProgress.stats.synced} profiles synced to Supabase
                  {actionProgress.stats.errors > 0 && ` • ${actionProgress.stats.errors} errors`}
                </p>
              )}
              {actionProgress.stats && actionProgress.action === 'Run Matching' && (
                <p className="text-xs text-lime-600 mt-1">
                  {actionProgress.stats.usersProcessed} users processed • {actionProgress.stats.strongMatches} strong matches
                </p>
              )}
            </div>
          </div>
        )}

        {loading && !diagnostics ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : diagnostics ? (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl border border-slate-100">
                <p className="text-2xl font-bold text-slate-900">{diagnostics.supabase?.total_jobs || 0}</p>
                <p className="text-xs text-slate-500">Total Jobs</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-lime-100">
                <p className="text-2xl font-bold text-lime-600">{diagnostics.supabase?.eligible_jobs || diagnostics.supabase?.total_jobs || 0}</p>
                <p className="text-xs text-slate-500">Eligible Jobs</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-blue-100">
                <p className="text-2xl font-bold text-blue-600">{diagnostics.supabase?.matchable_users || 0}</p>
                <p className="text-xs text-slate-500">Matchable Users</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-purple-100">
                <p className="text-2xl font-bold text-purple-600">{diagnostics.supabase?.total_match_results || 0}</p>
                <p className="text-xs text-slate-500">Match Results</p>
              </div>
            </div>

            {/* Issues */}
            {diagnostics.issues?.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Issues Detected
                </p>
                <ul className="text-sm text-amber-700 space-y-1">
                  {diagnostics.issues.map((issue, idx) => (
                    <li key={idx}>• {issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button
                variant="outline"
                onClick={() => handleAction('Sync Users', '/admin/matching/sync-all-users')}
                disabled={actionLoading === 'Sync Users'}
                className="justify-start"
              >
                {actionLoading === 'Sync Users' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2 text-blue-500" />
                )}
                Sync All Users
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction('Run Matching', '/admin/matching/run')}
                disabled={actionLoading === 'Run Matching'}
                className="justify-start"
              >
                {actionLoading === 'Run Matching' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2 text-lime-500" />
                )}
                Run Matching Job
              </Button>
              <Button
                variant="outline"
                onClick={loadDiagnostics}
                disabled={loading}
                className="justify-start"
              >
                <RefreshCw className={`h-4 w-4 mr-2 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500 text-center py-4">Failed to load diagnostics</p>
        )}
      </CardContent>
    </Card>
  );
};

// Stat Card Component
const StatCard = ({ title, value, change, changeType, icon: Icon, subtitle, color = "lime" }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-heading text-slate-900 mt-1">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              changeType === 'positive' ? 'text-lime-600' : changeType === 'negative' ? 'text-red-500' : 'text-slate-500'
            }`}>
              {changeType === 'positive' ? <ArrowUpRight className="h-4 w-4" /> : 
               changeType === 'negative' ? <ArrowDownRight className="h-4 w-4" /> : null}
              <span>{change}</span>
            </div>
          )}
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
          color === 'lime' ? 'bg-lime-100' : color === 'blue' ? 'bg-blue-100' : color === 'amber' ? 'bg-amber-100' : 'bg-slate-100'
        }`}>
          <Icon className={`h-6 w-6 ${
            color === 'lime' ? 'text-lime-600' : color === 'blue' ? 'text-blue-600' : color === 'amber' ? 'text-amber-600' : 'text-slate-600'
          }`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Funnel Component
const FunnelChart = ({ data }) => {
  const colors = ['#65a30d', '#84cc16', '#a3e635', '#d9f99d'];
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const width = (item.value / maxValue) * 100;
        const conversionRate = index > 0 ? ((item.value / data[index - 1].value) * 100).toFixed(0) : null;
        
        return (
          <div key={item.name} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">{item.name}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{item.value}</span>
                {conversionRate && (
                  <span className="text-slate-400 text-xs">({conversionRate}%)</span>
                )}
              </div>
            </div>
            <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
              <div 
                className="h-full rounded-lg transition-all duration-500"
                style={{ 
                  width: `${width}%`, 
                  backgroundColor: colors[index % colors.length]
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function AdminOverview() {
  const { authAxios, logout } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [signupData, setSignupData] = useState([]);
  const [loginData, setLoginData] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [metricsRes, activityRes, referralRes] = await Promise.all([
          authAxios.get('/admin/dashboard/metrics'),
          authAxios.get(`/admin/dashboard/activity?days=${timeRange}`),
          authAxios.get('/admin/referrals/stats')
        ]);
        
        setMetrics(metricsRes.data);
        setSignupData(activityRes.data.signups_by_day || []);
        setLoginData(activityRes.data.logins_by_day || []);
        setReferralStats(referralRes.data);
      } catch (error) {
        console.error('Failed to load dashboard:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [authAxios, timeRange]);

  const funnelData = metrics ? [
    { name: 'Registered', value: metrics.total_candidates },
    { name: 'Email Verified', value: metrics.email_verified },
    { name: 'Started Onboarding', value: metrics.onboarding_started },
    { name: 'Completed Profile', value: metrics.onboarding_complete }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading text-slate-900">Dashboard Overview</h1>
          <p className="text-slate-500">Monitor your platform&apos;s performance and user activity</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Candidates"
            value={metrics?.total_candidates || 0}
            change={`+${metrics?.new_this_week || 0} this week`}
            changeType="positive"
            icon={Users}
            color="lime"
          />
          <StatCard
            title="Completed Profiles"
            value={metrics?.onboarding_complete || 0}
            subtitle={`${metrics?.completion_rate || 0}% completion rate`}
            icon={UserCheck}
            color="blue"
          />
          <StatCard
            title="In Progress"
            value={metrics?.onboarding_in_progress || 0}
            subtitle="Incomplete profiles"
            icon={Clock}
            color="amber"
          />
          <StatCard
            title="New Today"
            value={metrics?.new_today || 0}
            change={metrics?.new_today > (metrics?.avg_daily_signups || 0) ? 'Above average' : 'Below average'}
            changeType={metrics?.new_today > (metrics?.avg_daily_signups || 0) ? 'positive' : 'neutral'}
            icon={UserPlus}
            color="slate"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Signups Chart */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Activity className="h-5 w-5 text-lime-600" />
                  New Signups
                </CardTitle>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={signupData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#65a30d" 
                      strokeWidth={2}
                      dot={{ fill: '#65a30d', strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Logins Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Daily Logins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={loginData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip 
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Funnel */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-lime-600" />
                Conversion Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FunnelChart data={funnelData} />
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/admin/candidates?status=ONBOARDING_IN_PROGRESS">
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Incomplete Profiles
                  </span>
                  <span className="flex items-center gap-1">
                    {metrics?.onboarding_in_progress || 0}
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </Button>
              </Link>
              <Link to="/admin/candidates?status=EMAIL_VERIFIED_PENDING_ONBOARDING">
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4 text-blue-500" />
                    Pending Onboarding
                  </span>
                  <span className="flex items-center gap-1">
                    {metrics?.pending_onboarding || 0}
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </Button>
              </Link>
              <Link to="/admin/analytics">
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-lime-600" />
                    View Full Analytics
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Referral Activity - Always visible */}
        <JobMatchingControls authAxios={authAxios} />
        
        {/* Referral Activity - Always visible */}
        <Card className="border-lime-200 bg-gradient-to-br from-lime-50/50 to-white mt-8">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Share2 className="h-5 w-5 text-lime-600" />
              Referral Activity
              <Badge variant="secondary" className="ml-2 text-xs">Viral Growth</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl border border-slate-100">
                <p className="text-3xl font-bold text-slate-900">{referralStats?.total_referrals || 0}</p>
                <p className="text-sm text-slate-500">Total Referrals</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-blue-100">
                <p className="text-3xl font-bold text-blue-600">{referralStats?.by_status?.SIGNED_UP || 0}</p>
                <p className="text-sm text-slate-500">Signed Up</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-lime-100">
                <p className="text-3xl font-bold text-lime-600">
                  {(referralStats?.by_status?.VERIFIED || 0) + (referralStats?.by_status?.ONBOARDED || 0)}
                </p>
                <p className="text-sm text-slate-500">Verified</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-amber-100">
                <p className="text-3xl font-bold text-amber-600">{referralStats?.conversion_rate || 0}%</p>
                <p className="text-sm text-slate-500">Conversion</p>
              </div>
            </div>

            {referralStats?.top_referrers?.length > 0 ? (
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Top Referrers</p>
                <div className="space-y-2">
                  {referralStats.top_referrers.slice(0, 5).map((referrer, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-lime-100 flex items-center justify-center text-sm font-bold text-lime-700">
                          {index + 1}
                        </div>
                        <span className="text-sm text-slate-900">{referrer.email}</span>
                      </div>
                      <span className="text-sm font-semibold text-lime-600">{referrer.referral_count} referrals</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 bg-white rounded-lg border border-dashed border-slate-200">
                <Share2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No referral activity yet</p>
                <p className="text-xs text-slate-400 mt-1">Referrals will appear here as users invite colleagues</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
